(function ($) {

    /**
     * Configuration Constants
     * Defines performance and behavior settings for the parallax effect.
     * These values control animation smoothness, responsiveness, and valid ranges.
     */
    const CONFIG = {
        THROTTLE: 16, // Limits animation to ~60fps
        DEBOUNCE: 100, // Delays resize calculations by 100ms
        MARGIN: '50px', // Extends observer visibility detection
        SPEED: 1, // Default parallax speed multiplier
        SPEED_RANGE: [-10, 10] // Restricts speed to valid range
    };

    /**
     * Internal State Management
     * Tracks elements, observer, and animation state.
     * Uses a Map for efficient element data storage and global variables for animation control.
     */
    const parallaxMap = new Map(); // Stores element-specific parallax data
    let observer = null; // IntersectionObserver for visibility detection
    let ticking = false; // Flags active animation loop
    let scrollY = window.scrollY; // Tracks current scroll position
    let resizeTimeout = null; // Debounce timer for resize events
    let lastScrollTime = 0; // Timestamp of last animation frame

    const cachedConstants = {
        windowHalfHeight: window.innerHeight / 2 // Caches window midpoint for calculations
    };

    /**
     * Fallback for Unsupported Browsers
     * Checks for IntersectionObserver support and disables plugin if unavailable.
     */
    if (!('IntersectionObserver' in window)) {
        console.warn('[Parallax] IntersectionObserver not supported. Plugin disabled.');
        return;
    }

    /**
     * Main Animation Loop
     * Updates element positions based on scroll position and speed.
     * Uses requestAnimationFrame for smooth rendering and throttling for performance.
     */
    function updateParallax(timestamp) {
        if (!ticking) return; // Exit if animation is paused

        if (timestamp - lastScrollTime < CONFIG.THROTTLE) { // Skip frame if too soon
            requestAnimationFrame(updateParallax);
            return;
        }

        lastScrollTime = timestamp; // Update last frame time
        scrollY = window.scrollY; // Refresh scroll position

        const updates = []; // Collects transform updates

        parallaxMap.forEach((data, el) => { // Process each parallax element
            if (!data.visible || data.speed === 0) return; // Skip invisible or static elements

            const rect = el.getBoundingClientRect(); // Get element position
            const distanceFromTop = rect.top + scrollY; // Calculate top relative to document
            const center = scrollY + cachedConstants.windowHalfHeight; // Window midpoint

            let targetTranslateY = -(distanceFromTop - center + data.rectHalfHeight) * (data.speed / 10); // Compute parallax offset

            if (data.contain) { // Apply containment bounds if enabled
                const { contentHeight, paddingTop, paddingBottom } = data.parentDims;
                const maxTranslateY = (data.height - contentHeight) / 2 - paddingBottom; // Upper bound
                const minTranslateY = -(data.height - contentHeight) / 2 + paddingTop; // Lower bound
                targetTranslateY = Math.max(minTranslateY, Math.min(maxTranslateY, targetTranslateY)); // Clamp translation
            }

            if (typeof data.currentTranslateY !== 'number') { // Initialize translation
                data.currentTranslateY = targetTranslateY;
            } else {
                data.currentTranslateY += (targetTranslateY - data.currentTranslateY) * 0.15; // Smooth transition
            }

            const roundedTranslateY = Math.round(data.currentTranslateY * 10) / 10; // Round for performance
            updates.push({ el, translateY: roundedTranslateY }); // Queue update
        });

        requestAnimationFrame(() => { // Apply transforms in batch
            updates.forEach(({ el, translateY }) => {
                el.style.transform = `translateY(${translateY}px)`; // Update element position
            });
        });

        requestAnimationFrame(updateParallax); // Continue animation loop
    }

    /**
     * Resize Event Handler
     * Recalculates dimensions for window and elements on resize.
     * Debounces to prevent excessive calculations during rapid resize events.
     */
    function onResize() {
        clearTimeout(resizeTimeout); // Clear existing timer
        resizeTimeout = setTimeout(() => { // Debounce resize
            cachedConstants.windowHalfHeight = window.innerHeight / 2; // Update window midpoint

            parallaxMap.forEach((data, el) => { // Update each element's dimensions
                data.height = el.offsetHeight; // Refresh element height
                data.rectHalfHeight = el.offsetHeight / 2; // Update midpoint

                if (data.contain) { // Recalculate parent dimensions if contained
                    data.parentDims = {
                        contentHeight: data.$parent.height(), // Parent height
                        paddingTop: parseFloat(data.$parent.css('padding-top')) || 0, // Top padding
                        paddingBottom: parseFloat(data.$parent.css('padding-bottom')) || 0 // Bottom padding
                    };
                }
            });
        }, CONFIG.DEBOUNCE); // Use configured debounce delay
    }

    /**
     * IntersectionObserver Callback
     * Manages element visibility and animation loop activation.
     * Optimizes performance by only animating visible elements.
     */
    function onIntersect(entries) {
        entries.forEach(entry => { // Process each observed element
            const el = entry.target; // Get element
            const data = parallaxMap.get(el); // Retrieve parallax data
            if (!data) return; // Skip if no data

            data.visible = entry.isIntersecting; // Update visibility
            el.style.willChange = entry.isIntersecting ? 'transform' : 'auto'; // Optimize rendering
        });

        if (!ticking && entries.some(entry => entry.isIntersecting)) { // Start animation if needed
            ticking = true;
            requestAnimationFrame(updateParallax); // Begin animation loop
        }
    }

    /**
     * jQuery Plugin Definition
     * Initializes parallax effect on selected elements.
     * Configures observer, handles element setup, and supports custom attributes.
     */
    $.fn.parallax = function () {
        if (!observer) { // Initialize observer if not set
            observer = new IntersectionObserver(onIntersect, {
                root: null, // Use viewport
                rootMargin: CONFIG.MARGIN, // Extend visibility detection
                threshold: 0 // Trigger on any visibility
            });
            window.addEventListener('resize', onResize); // Attach resize handler
        }

        this.each(function () { // Process each selected element
            const $el = $(this); // jQuery element
            const speedAttr = parseFloat($el.attr('data-parallax-speed')); // Speed attribute
            const heightAttr = parseFloat($el.attr('data-parallax-height')); // Height attribute
            const containAttr = $el.attr('data-parallax-contain'); // Containment attribute

            const speed = isNaN(speedAttr) // Validate and clamp speed
                ? CONFIG.SPEED
                : Math.max(CONFIG.SPEED_RANGE[0], Math.min(CONFIG.SPEED_RANGE[1], speedAttr));

            if (speed === 0) return; // Skip if no movement

            let $parent = $el.parent(); // Default to immediate parent
            if (containAttr && containAttr !== 'true') { // Check for custom parent
                $parent = $el.closest(containAttr); // Find specified parent
                if ($parent.length === 0) { // Warn if parent not found
                    console.warn(`[Parallax] Could not find containing element: ${containAttr}`);
                    $parent = $el.parent(); // Fallback to immediate parent
                }
            }

            const heightMultiplier = isNaN(heightAttr) ? 1 : heightAttr; // Get height multiplier
            if (heightMultiplier !== 1) { // Apply custom height if specified
                const newHeight = heightMultiplier * 100; // Scale height
                const newTop = (1 - heightMultiplier) * 100 / 2; // Center vertically
                $el.css({
                    height: `${newHeight}%`, // Set new height
                    top: `${newTop}%`, // Position vertically
                    position: 'absolute' // Enable positioning
                });
            }

            const data = { // Store element data
                speed, // Parallax speed
                $parent, // Parent element
                contain: !!containAttr, // Containment flag
                visible: false, // Initial visibility
                height: $el[0].offsetHeight, // Element height
                rectHalfHeight: $el[0].offsetHeight / 2, // Element midpoint
                currentTranslateY: null, // Current translation
                parentDims: containAttr ? { // Parent dimensions if contained
                    contentHeight: $parent.height(),
                    paddingTop: parseFloat($parent.css('padding-top')) || 0,
                    paddingBottom: parseFloat($parent.css('padding-bottom')) || 0
                } : {}
            };

            parallaxMap.set($el[0], data); // Register element
            observer.observe($el[0]); // Start observing
        });

        return this; // Maintain jQuery chainability
    };

})(jQuery);

/**
 * DOM Ready Handler
 * Automatically applies parallax to elements with .parallax class.
 */
// $(document).ready(() => $('.parallax').parallax()); // Initialize on DOM ready